/**
 * SPLH Dashboard Data Service
 *
 * Handles data fetching for the SPLH Performance Dashboard.
 * Provides GraphQL query execution with client-side caching.
 *
 * Architecture:
 * - Only passes clientId and dateRange to backend
 * - Backend handles ALL calculations and aggregations
 * - Frontend receives ready-to-render data
 * - Client-side caching with 5-minute TTL to prevent redundant requests
 *
 * @module pages/common/dashboards/SPLHDashboard/services/splhDataService
 */

import { API, graphqlOperation } from 'aws-amplify';
import * as logger from '../../../../../utils/logger';
import { getSPLHDashboardData as getSPLHDashboardDataQuery } from './splhQueries';

/**
 * Cache configuration
 * @constant {number}
 */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * In-memory cache for dashboard data
 * Key format: `${clientId}:${startDate}:${endDate}`
 * @type {Map<string, { data: Object, timestamp: number }>}
 */
const dashboardCache = new Map();

/**
 * Generates a cache key from query parameters
 *
 * @param {string} clientId - Client identifier
 * @param {Object} dateRange - Date range object
 * @param {string} dateRange.startDate - Start date (YYYY-MM-DD)
 * @param {string} dateRange.endDate - End date (YYYY-MM-DD)
 * @returns {string} Cache key
 * @private
 */
function getCacheKey(clientId, dateRange) {
  return `${clientId}:${dateRange.startDate}:${dateRange.endDate}`;
}

/**
 * Checks if a cached entry is still valid
 *
 * @param {{ data: Object, timestamp: number }} cacheEntry - Cached entry
 * @returns {boolean} True if cache is still valid
 * @private
 */
function isCacheValid(cacheEntry) {
  if (!cacheEntry) return false;
  const age = Date.now() - cacheEntry.timestamp;
  return age < CACHE_TTL_MS;
}

/**
 * Extracts user-friendly error message from GraphQL errors
 *
 * @param {Error|Object} error - Error object from GraphQL response
 * @returns {string} User-friendly error message
 * @private
 */
function extractErrorMessage(error) {
  // Handle GraphQL errors array
  if (error?.errors?.length > 0) {
    const firstError = error.errors[0];
    return firstError.message || 'An error occurred while fetching dashboard data';
  }

  // Handle standard Error objects
  if (error?.message) {
    // Don't expose internal error details to users
    if (error.message.includes('Network') || error.message.includes('network')) {
      return 'Unable to connect to the server. Please check your connection and try again.';
    }
    if (error.message.includes('timeout') || error.message.includes('Timeout')) {
      return 'The request timed out. Please try again.';
    }
    if (error.message.includes('unauthorized') || error.message.includes('Unauthorized')) {
      return 'You are not authorized to view this dashboard. Please sign in again.';
    }
    // Return generic message for other errors
    return 'Unable to load dashboard data. Please try again.';
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Validates the input parameters for the dashboard query
 *
 * @param {string} clientId - Client identifier
 * @param {Object} dateRange - Date range object
 * @returns {{ isValid: boolean, error: string|null }} Validation result
 * @private
 */
function validateInput(clientId, dateRange) {
  if (!clientId || typeof clientId !== 'string') {
    return { isValid: false, error: 'Client ID is required' };
  }

  if (!dateRange || typeof dateRange !== 'object') {
    return { isValid: false, error: 'Date range is required' };
  }

  if (!dateRange.startDate || !dateRange.endDate) {
    return { isValid: false, error: 'Start date and end date are required' };
  }

  // Validate date format (YYYY-MM-DD)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateRange.startDate) || !dateRegex.test(dateRange.endDate)) {
    return { isValid: false, error: 'Dates must be in YYYY-MM-DD format' };
  }

  // Validate date order
  if (dateRange.startDate > dateRange.endDate) {
    return { isValid: false, error: 'Start date must be before or equal to end date' };
  }

  return { isValid: true, error: null };
}

/**
 * Fetches SPLH Dashboard data from the backend
 *
 * This function:
 * - Validates input parameters
 * - Checks cache for existing valid data
 * - Executes GraphQL query if cache miss
 * - Caches successful responses
 * - Returns ready-to-render data from backend
 *
 * @param {string} clientId - Client identifier (e.g., 'dem50')
 * @param {Object} dateRange - Date range for the query
 * @param {string} dateRange.startDate - Start date in YYYY-MM-DD format
 * @param {string} dateRange.endDate - End date in YYYY-MM-DD format
 * @param {Object} [options={}] - Optional configuration
 * @param {boolean} [options.skipCache=false] - Skip cache and force fresh fetch
 * @returns {Promise<Object>} Dashboard data with kpis, trendData, hierarchyData, breakdownData, alerts, hierarchyMetadata
 * @throws {Error} If validation fails or the query fails
 *
 * @example
 * // Fetch dashboard data for last 7 days
 * const data = await fetchSPLHDashboardData('dem50', {
 *   startDate: '2025-11-18',
 *   endDate: '2025-11-24'
 * });
 *
 * @example
 * // Force fresh fetch (skip cache)
 * const freshData = await fetchSPLHDashboardData('dem50', dateRange, { skipCache: true });
 */
export async function fetchSPLHDashboardData(clientId, dateRange, options = {}) {
  const { skipCache = false } = options;

  // Validate input
  const validation = validateInput(clientId, dateRange);
  if (!validation.isValid) {
    logger.warn('Invalid input for fetchSPLHDashboardData', {
      clientId,
      dateRange,
      error: validation.error,
    });
    throw new Error(validation.error);
  }

  const cacheKey = getCacheKey(clientId, dateRange);

  // Check cache (unless skipCache is true)
  if (!skipCache) {
    const cached = dashboardCache.get(cacheKey);
    if (isCacheValid(cached)) {
      logger.debug('Returning cached SPLH dashboard data', {
        clientId,
        dateRange,
        cacheAge: Date.now() - cached.timestamp,
      });
      return cached.data;
    }
  }

  logger.debug('Fetching SPLH dashboard data', { clientId, dateRange });

  try {
    const response = await API.graphql(
      graphqlOperation(getSPLHDashboardDataQuery, {
        input: {
          clientId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        },
      })
    );

    const data = response?.data?.getSPLHDashboardData;

    if (!data) {
      logger.warn('Empty response from getSPLHDashboardData', { clientId, dateRange });
      throw new Error('No data returned from server');
    }

    // Cache the successful response
    dashboardCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });

    logger.debug('Successfully fetched SPLH dashboard data', {
      clientId,
      dateRange,
      hasKpis: Boolean(data.kpis),
      trendDataPoints: data.trendData?.length || 0,
      hierarchyNodes: data.hierarchyData?.length || 0,
      alertCount: data.alerts?.length || 0,
    });

    return data;
  } catch (error) {
    // If we threw our own error (like "No data returned"), re-throw it directly
    if (error.message === 'No data returned from server') {
      throw error;
    }

    const userMessage = extractErrorMessage(error);

    logger.error('Failed to fetch SPLH dashboard data', {
      clientId,
      dateRange,
      error: error?.message || error,
      graphqlErrors: error?.errors,
    });

    throw new Error(userMessage);
  }
}

/**
 * Clears the dashboard cache
 *
 * Call this when data is known to be stale (e.g., after a mutation)
 * or when the user explicitly requests a refresh.
 *
 * @param {string} [clientId] - Optional client ID to clear only that client's cache
 * @returns {void}
 *
 * @example
 * // Clear all cached data
 * clearCache();
 *
 * @example
 * // Clear only dem50's cached data
 * clearCache('dem50');
 */
export function clearCache(clientId) {
  if (clientId) {
    // Clear only entries for the specified client
    for (const key of dashboardCache.keys()) {
      if (key.startsWith(`${clientId}:`)) {
        dashboardCache.delete(key);
      }
    }
    logger.debug('Cleared SPLH dashboard cache for client', { clientId });
  } else {
    // Clear all cached data
    dashboardCache.clear();
    logger.debug('Cleared all SPLH dashboard cache');
  }
}

/**
 * Gets cache statistics for debugging
 *
 * @returns {{ size: number, keys: string[] }} Cache statistics
 *
 * @example
 * const stats = getCacheStats();
 * console.log(`Cache has ${stats.size} entries`);
 */
export function getCacheStats() {
  return {
    size: dashboardCache.size,
    keys: Array.from(dashboardCache.keys()),
  };
}
